import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Layout from "components/Layout";
import OfferDetailsModal from "components/OfferDetailsModal";
import VoucherBalanceOption from "./VoucherBalanceOption";
import api from "utils/api";
import { setUser } from "state";
import {
  SHOP_PRODUCTS,
  formatShopPrice,
  quoteShopVoucher,
} from "constants/shopVouchers";
import {
  checkoutVoucherItems,
  isValidShopEmail,
  normalizeShopEmail,
  readShopBasket,
  readShopGuestEmail,
  writeShopBasket,
  writeShopGuestEmail,
} from "utils/shopBasket";
import "styles/booking.css";
import "styles/shop.css";

const ShopPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((s) => s.user);
  const token = useSelector((s) => s.token);
  const location = useLocation();
  const shopConfigRef = useRef(null);
  const [openOfferDetails, setOpenOfferDetails] = useState(null);
  const shopParams = new URLSearchParams(location.search);
  const requestedMassage = shopParams.get("massage");
  const requestedPersons = Number(shopParams.get("persons"));
  const requestedDuration = Number(shopParams.get("duration"));
  const [selectedId, setSelectedId] = useState(
    SHOP_PRODUCTS.some((item) => item.id === requestedMassage) ? requestedMassage : null
  );
  const [delivery, setDelivery] = useState(
    shopParams.get("delivery") === "onsite" ? "onsite" : "studio"
  );
  const [personCount, setPersonCount] = useState(
    Number.isInteger(requestedPersons) && requestedPersons >= 1 && requestedPersons <= 10
      ? requestedPersons
      : 1
  );
  const [durationMinutes, setDurationMinutes] = useState(
    requestedDuration === 30 || requestedDuration === 60 ? requestedDuration : ""
  );
  const [gift, setGift] = useState(shopParams.get("gift") === "1");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [guestEmail, setGuestEmail] = useState(readShopGuestEmail);
  const [basket, setBasket] = useState(readShopBasket);
  const [status, setStatus] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [applyBalance, setApplyBalance] = useState(true);
  const [purchased, setPurchased] = useState([]);
  const balanceCents = token ? Math.max(0, Number(user?.referralBalanceCents || 0)) : 0;

  const product = SHOP_PRODUCTS.find((item) => item.id === selectedId) || null;
  const quote = useMemo(() => {
    if (!product) return null;
    if (product.asksDuration && !durationMinutes) return null;
    return quoteShopVoucher({
      massageType: product.id,
      delivery,
      personCount,
      durationMinutes,
    });
  }, [delivery, durationMinutes, personCount, product]);

  useEffect(() => {
    if (!selectedId) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const target = shopConfigRef.current?.querySelector(
        ".booking-delivery-choice"
      );
      if (!target) return;
      const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
      target.scrollIntoView({
        behavior: reduceMotion ? "auto" : "smooth",
        block: "center",
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [selectedId]);

  const replaceBasket = (next) => {
    setBasket(writeShopBasket(next));
  };

  const startCheckout = async (items) => {
    if (!token && !isValidShopEmail(guestEmail)) {
      setStatus({ kind: "error", text: "Bitte gib eine gültige E-Mail-Adresse für den Kauf an." });
      return;
    }
    setCheckingOut(true);
    setStatus(null);
    try {
      const result = await checkoutVoucherItems(
        items,
        token ? undefined : guestEmail,
        Boolean(token && applyBalance && balanceCents > 0)
      );
      if (result.url) window.location.href = result.url;
      else navigate(`/shop?order_id=${encodeURIComponent(result.orderId)}`);
    } catch (err) {
      setStatus({
        kind: "error",
        text: err?.response?.data?.message || err?.message || "Der Kauf konnte nicht gestartet werden.",
      });
      setCheckingOut(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    const orderId = params.get("order_id");
    const wasCancelled = Boolean(params.get("cancelled"));
    if (wasCancelled) {
      setStatus({ kind: "info", text: "Der Kauf wurde abgebrochen. Dein Warenkorb bleibt erhalten." });
    }
    if (wasCancelled && (!orderId || !token)) return undefined;
    if (!sessionId && !orderId) return undefined;
    let cancelled = false;
    api
      .post(
        wasCancelled ? "/vouchers/cancel" : "/vouchers/confirm",
        sessionId && !wasCancelled ? { sessionId } : { orderId }
      )
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.confirmed) {
          if (token) {
            api.get("/auth/me").then(({ data: account }) => {
              if (!cancelled && account?.user) dispatch(setUser({ user: account.user }));
            }).catch(() => {});
          }
          setPurchased(data.vouchers || []);
          replaceBasket([]);
          writeShopGuestEmail("");
          setGuestEmail("");
          setStatus({
            kind: "success",
            text: !sessionId && !wasCancelled
              ? "Gutscheine mit Guthaben bezahlt. Du erhältst sie per E-Mail."
              : "Zahlung erfolgreich. Du erhältst die Gutscheine per E-Mail.",
          });
        } else if (wasCancelled && data?.cancelled) {
          setStatus({
            kind: "info",
            text: "Der Kauf wurde abgebrochen. Falls du Guthaben verwendet hast, ist es wieder verfügbar.",
          });
          api.get("/auth/me").then(({ data: account }) => {
            if (!cancelled && account?.user) dispatch(setUser({ user: account.user }));
          }).catch(() => {});
        } else {
          setStatus({
            kind: "info",
            text: "Die Zahlung wird noch verarbeitet. Die E-Mail folgt, sobald sie bestätigt ist.",
          });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus({
          kind: "error",
          text: err?.response?.data?.message || "Der Kauf konnte noch nicht bestätigt werden.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [location.search, dispatch, token]);

  const buildItem = () => {
    if (!product || !quote) return { error: "Bitte wähle Dauer oder Personenanzahl." };
    if (product.asksDuration && !durationMinutes) {
      return { error: "Bitte wähle eine Dauer." };
    }
    const email = normalizeShopEmail(recipientEmail);
    const purchaserEmail = normalizeShopEmail(token ? user?.email : guestEmail);
    const giftToOther =
      gift && email && email !== purchaserEmail;
    if (gift && !giftToOther && email && email === purchaserEmail) {
      return { error: "Zum Verschenken braucht es eine andere E-Mail-Adresse." };
    }
    if (gift && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return { error: "Bitte gib die E-Mail der beschenkten Person an." };
    }
    return {
      item: {
        localId: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        massageType: quote.massageType,
        delivery: quote.delivery,
        personCount: quote.personCount,
        durationMinutes: quote.durationMinutes,
        priceCents: quote.priceCents,
        label: quote.label,
        recipientEmail: giftToOther ? email : "",
      },
    };
  };

  const addToBasket = () => {
    const built = buildItem();
    if (built.error) {
      setStatus({ kind: "error", text: built.error });
      return;
    }
    replaceBasket([...basket, built.item]);
    setStatus({ kind: "success", text: "In den Warenkorb gelegt." });
    setGift(false);
    setRecipientEmail("");
  };

  const buyNow = () => {
    const built = buildItem();
    if (built.error) {
      setStatus({ kind: "error", text: built.error });
      return;
    }
    startCheckout([built.item]);
  };

  return (
    <Layout>
      <section className="shop-page">
        <header className="shop-header">
          <h1>
            Massage-<em>Gutscheine</em>
          </h1>
          <p>
            Wake-Up, Classic und Intense für Privatpersonen. Du bezahlst den Gutschein
            online oder mit deinem Guthaben und löst den Code später im Konto ein.
          </p>
        </header>

        <div className="home-offer-grid home-offer-grid--private shop-massage-grid">
          {SHOP_PRODUCTS.map((item) => {
            const selected = selectedId === item.id;
            const visualId = item.id === "intense" ? "premium" : item.id;
            return (
              <article
                key={item.id}
                className={`home-offer-card home-offer-card--${visualId} ${
                  item.id === "classic" ? "is-featured" : ""
                } ${selected ? "is-selected" : ""}`}
              >
                <div className="home-offer-card-content">
                  <h3>{item.title}</h3>
                  {item.subtitle && <p className="home-offer-subtitle">{item.subtitle}</p>}
                  <div className="home-offer-card-actions">
                    <button
                      type="button"
                      className="offer-details-trigger"
                      onClick={() =>
                        setOpenOfferDetails(item.id === "intense" ? "intense" : item.id)
                      }
                    >
                      Mehr erfahren
                    </button>
                    <button
                      type="button"
                      className={`home-offer-booking-link shop-select-button ${
                        selected ? "is-selected" : ""
                      }`}
                      onClick={() => {
                        setSelectedId(selected ? null : item.id);
                        setStatus(null);
                      }}
                    >
                      {selected ? "Auswahl aktiv" : "Gutschein wählen"}
                    </button>
                  </div>
                </div>
                <div className="home-offer-price">
                  {item.priceLabel}
                  {item.priceNote && <small>{item.priceNote}</small>}
                </div>
              </article>
            );
          })}
        </div>

        {product && (
          <div className="option-detail" ref={shopConfigRef}>
            <div className="booking-step">
              <div className="step-head">
                <h2>
                  {product.title} — <em>Gutschein</em>
                </h2>
              </div>
              <form
                className="shop-config"
                onSubmit={(event) => {
                  event.preventDefault();
                  addToBasket();
                }}
              >
                <div className="booking-delivery-choice" aria-label={`${product.title} Ort`}>
                  <strong>Sollen wir zu dir kommen?</strong>
                  <div className="booking-delivery-options">
                    <label className={delivery === "studio" ? "is-selected" : ""}>
                      <input
                        type="radio"
                        name="shop-delivery"
                        checked={delivery === "studio"}
                        onChange={() => setDelivery("studio")}
                      />
                      Nein, im Studio
                    </label>
                    <label className={delivery === "onsite" ? "is-selected" : ""}>
                      <input
                        type="radio"
                        name="shop-delivery"
                        checked={delivery === "onsite"}
                        onChange={() => setDelivery("onsite")}
                      />
                      <span>Ja, bitte vor Ort</span>
                      <span className="booking-delivery-badge">+ 39 € Anfahrt</span>
                    </label>
                  </div>
                </div>

                {product.asksPersonCount && (
                  <label className="shop-field">
                    Personen
                    <select
                      value={personCount}
                      onChange={(event) => setPersonCount(Number(event.target.value))}
                    >
                      {Array.from({ length: 10 }, (_, index) => index + 1).map((count) => (
                        <option key={count} value={count}>
                          {count}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                {product.asksDuration && (
                  <label className="shop-field">
                    Dauer
                    <select
                      value={durationMinutes}
                      onChange={(event) => setDurationMinutes(event.target.value)}
                    >
                      <option value="">Bitte wählen…</option>
                      {product.durationOptions.map((minutes) => (
                        <option key={minutes} value={minutes}>
                          {minutes} Min.
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <label className="shop-gift">
                  <input
                    type="checkbox"
                    checked={gift}
                    onChange={(event) => setGift(event.target.checked)}
                  />
                  <span>Als Geschenk an eine andere E-Mail senden</span>
                </label>
                {gift && (
                  <label className="shop-field">
                    E-Mail der beschenkten Person
                    <input
                      type="email"
                      value={recipientEmail}
                      placeholder="name@example.com"
                      onChange={(event) => setRecipientEmail(event.target.value)}
                    />
                  </label>
                )}

                {!token && (
                  <label className="shop-field">
                    Deine E-Mail-Adresse für die Kaufbestätigung
                    <input
                      type="email"
                      value={guestEmail}
                      placeholder="name@example.com"
                      autoComplete="email"
                      onChange={(event) => {
                        setGuestEmail(event.target.value);
                        writeShopGuestEmail(event.target.value);
                      }}
                    />
                  </label>
                )}

                <p className="shop-price">
                  {quote ? formatShopPrice(quote.priceCents) : "Bitte Auswahl vervollständigen"}
                  {quote?.travelCents ? <small> inklusive Anfahrt</small> : null}
                </p>

                <VoucherBalanceOption
                  totalCents={quote?.priceCents || 0}
                  balanceCents={balanceCents}
                  checked={applyBalance}
                  onChange={setApplyBalance}
                />

                <div className="shop-actions">
                  <button type="submit" className="btn-outline" disabled={checkingOut || !quote}>
                    In den Warenkorb
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={checkingOut || !quote}
                    onClick={buyNow}
                  >
                    {checkingOut ? "Weiterleitung…" : "Direkt kaufen"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {status && <div className={`auth-alert is-${status.kind}`}>{status.text}</div>}

        {purchased.length > 0 && (
          <div className="shop-purchased">
            <h2>Deine Codes</h2>
            <ul>
              {purchased.map((voucher) => (
                <li key={voucher.id}>
                  <strong>{voucher.code}</strong>
                  <span>{voucher.label}</span>
                  {voucher.isGift && <span>Geschenk an {voucher.recipientEmail}</span>}
                </li>
              ))}
            </ul>
            <p>
              Löse den Code unter <Link to="/konto/gutscheine">Gutscheine</Link> ein. Geschenkte
              Codes stehen auch in der E-Mail an die beschenkte Person.
            </p>
          </div>
        )}
      </section>
      <OfferDetailsModal
        offerId={openOfferDetails}
        onClose={() => setOpenOfferDetails(null)}
      />
    </Layout>
  );
};

export default ShopPage;
